/**
 * What a job *did*, in the operator's words.
 *
 * The dashboard used to headline each activity card with its `job_01M0…` ULID.
 * That is our internal correlation handle used as a title: five cards in a row
 * that an operator cannot tell apart, none of them answering the only question
 * they had — what happened, to how many SIMs, and when.
 *
 * It is the same principle the estate table already applies: **identifiers are
 * for correlation, not for headlines.** The ref stays available — it is what
 * someone quotes in a support thread — but it is secondary, not the first thing
 * the eye lands on.
 *
 * The verbs are **hand-written per job type**, not derived from the enum's
 * spelling. `SIM_ACTIVATE_FROM_TEST` prettified mechanically reads "Sim Activate
 * From Test"; what an operator calls it is "Moved to a live plan".
 */

import type { ClientJob, JobType } from '@rd/shared';

/** Past tense, because a job in the activity list has already happened. */
const VERB: Record<JobType, { one: string; many: string }> = {
  SIM_ACTIVATE: { one: 'Activated', many: 'Activated' },
  SIM_ACTIVATE_TEST: { one: 'Started test mode on', many: 'Started test mode on' },
  SIM_ACTIVATE_FROM_TEST: { one: 'Moved to a live plan', many: 'Moved to a live plan' },
  SIM_SWAP: { one: 'Swapped', many: 'Swapped' },
  SIM_SUSPEND: { one: 'Put into safe custody', many: 'Put into safe custody' },
  SIM_RESUME: { one: 'Took out of safe custody', many: 'Took out of safe custody' },
  SIM_TEMP_DISCONNECT: { one: 'Disconnected', many: 'Disconnected' },
  SIM_TD_RESUME: { one: 'Reconnected', many: 'Reconnected' },
  SIM_CHANGE_PLAN: { one: 'Changed the plan on', many: 'Changed the plan on' },
  KYC_BULK_UPLOAD: { one: 'Uploaded KYC for', many: 'Uploaded KYC for' },
  ARP_BULK_UPLOAD: { one: 'Uploaded ARP for', many: 'Uploaded ARP for' },
  LOCATION_WHITELIST: { one: 'Whitelisted the location of', many: 'Whitelisted the location of' },
  SMS_SEND: { one: 'Sent a message to', many: 'Sent messages to' },
  SMS_SEND_CUSTOM: { one: 'Sent a custom message to', many: 'Sent custom messages to' },
  SMS_SEND_BATCH: { one: 'Sent a batch of messages', many: 'Sent a batch of messages' },
  SMS_WHITELIST_BATCH: { one: 'Updated the SMS whitelist on', many: 'Updated the SMS whitelist on' },
  VOICE_WHITELIST_UPDATE: { one: 'Updated the voice whitelist on', many: 'Updated the voice whitelist on' },
};

/**
 * The headline: the action and how many SIMs it touched.
 *
 * `totalCount` is 0 for a batch upload whose size the network decides, so the
 * count is omitted rather than rendered as "0 SIMs" — which would be a claim we
 * cannot support.
 */
export function jobHeadline(job: ClientJob): string {
  /**
   * `VERB` is `Record<JobType, …>`, so **adding a job type without a verb is a
   * compile error**. There is deliberately no `?? 'Ran a job on'` fallback: a
   * missing verb that renders as a plausible generic sentence is invisible
   * until somebody reads a card, which is the indistinguishability class — the
   * failing world looking exactly like the working one.
   *
   * The runtime guard exists only for data that violates the type (an upstream
   * sending a status this build has never heard of), and it is deliberately
   * ugly: it shows the raw type so the gap names itself rather than hiding
   * behind English.
   */
  const verb = VERB[job.type];
  if (!verb) return `Ran ${job.type} on ${job.counts.total} SIM(s)`;
  const count = job.counts.total;

  if (count === 0) return verb.one;
  if (count === 1) return `${verb.one} 1 SIM`;
  return `${verb.many} ${count} SIMs`;
}

/**
 * A relative time an operator can read at a glance. Absolute timestamps are
 * for the detail page; on a list, "4 minutes ago" is what answers "is this the
 * one I just did?".
 */
export function jobWhen(job: ClientJob, now: number = Date.now()): string {
  const iso = job.completedAt ?? job.submittedAt;
  if (!iso) return 'just now';

  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 60) return 'just now';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
